import {useContext, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {
  CareJourneyDetailsResponse,
  JourneySource,
  ModuleType,
  UserTaskType,
} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import Helpers from '@/helpers/helpers';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import RejectReferralModal from '@/components/pages/tasks/task_edit/progress/pre_review/RejectReferralModal';

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
  rosterId: number;
  disabled: boolean;
  invalidate: () => void;
};

// Terminal step statuses — a pre-review USER_TASK in one of these is no longer the active review, so
// there is nothing to reject. Mirrors the status strings TaskEditProgress uses (statusDisplayValues:
// pending/in_progress/skipped/completed); an active step is the non-completed/non-skipped one.
const TERMINAL_STEP_STATUSES = new Set(['completed', 'skipped']);

// True when the journey still has an open CARE_JOURNEY_PRE_REVIEW USER_TASK step. Mirrors the
// preReviewIndex lookup in TaskEditProgress (moduleType === USER_TASK && userTaskType ===
// CARE_JOURNEY_PRE_REVIEW), with the added guard that the step has not reached a terminal status.
function hasActivePreReviewStep(careJourneyDetails: CareJourneyDetailsResponse): boolean {
  return careJourneyDetails.steps.some(
    step =>
      step.moduleType === ModuleType.USER_TASK
      && step.userTaskType === UserTaskType.CARE_JOURNEY_PRE_REVIEW
      && !TERMINAL_STEP_STATUSES.has(step.status),
  );
}

/**
 * Header action (CARECO-5768) that lets a navigator reject a provider referral that is still pending
 * pre-review. Self-contained: it owns the reject modal state and mutation and self-gates visibility
 * from careJourneyDetails alone, so it can sit beside the status dropdown in the task-detail header
 * without the host page knowing anything about the reject flow. Renders nothing unless this is a
 * FLOWABLE journey with an active pre-review USER_TASK step and the journey is not closed/cancelled.
 */
export default function ReferralRejectHeaderAction({
  careJourneyDetails,
  rosterId,
  disabled,
  invalidate,
}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const {addAlert} = useAlertStore();
  const [rejectOpen, setRejectOpen] = useState(false);

  // CARECO-5768: reject the referral with a reason (backend derives the active pre-review task).
  const {mutate: reject, isPending} = useMutation({
    mutationFn: async (input: {rejectionReasonCode: string; rejectionReasonText?: string}) =>
      carecoApi?.rejectReferral(careJourneyDetails.careJourneyInstanceId, rosterId, {
        rejectionReasonCode: input.rejectionReasonCode,
        rejectionReasonText: input.rejectionReasonText,
      }),
    onSuccess() {
      addAlert({type: ToastType.SUCCESS, content: 'Referral rejected.'});
      invalidate();
      setRejectOpen(false);
    },
    onError(error: AxiosError) {
      // Keep the modal open on failure so the selected reason / entered detail are preserved for
      // retry; it is closed only on success (CARECO-5768).
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const journeyStatus = careJourneyDetails.status?.toLowerCase();
  const isClosed = journeyStatus === 'closed' || journeyStatus === 'cancelled';

  if (
    careJourneyDetails.journeySource !== JourneySource.FLOWABLE
    || isClosed
    || !hasActivePreReviewStep(careJourneyDetails)
  ) {
    return null;
  }

  return (
    <>
      <Button
        styleType={StyleType.SECONDARY}
        onClick={() => setRejectOpen(true)}
        disabled={disabled || isPending}
      >
        Reject Referral
      </Button>
      {rejectOpen && (
        <RejectReferralModal
          isOpen
          onClose={() => setRejectOpen(false)}
          onConfirm={(code, text) => reject({rejectionReasonCode: code, rejectionReasonText: text})}
          isPending={isPending}
        />
      )}
    </>
  );
}
